import React, { useState } from "react";
import {
  Box, TextField, Button, Snackbar, Paper, Typography,
  Dialog, DialogActions, DialogContent, DialogTitle, Stack, CircularProgress
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeletableTradeCard from "../../components/admin/AdminDeleteTradeCard";
import { fetchWithAuth } from "../../utils/api";

const AdminDeleteTrade = () => {
  const [input, setInput] = useState("");
  const [trades, setTrades] = useState([]);
  const [snackbar, setSnackbar] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const val = e.target.value.replace(/[^0-9, ]/g, ""); // allow spaces
    setInput(val);
  };

  const fetchTrades = async () => {
    let ids = input.split(",").map(id => id.trim()).filter(Boolean);
    if (ids.length === 0) return;

    const currentIds = trades.map(t => t.tradeId);
    const newIds = ids.filter(id => !currentIds.includes(Number(id)));

    if (trades.length + newIds.length > 10) {
      setSnackbar("The list to delete cannot exceed 10 trades at a time!");
      setInput("");
      return;
    }

    try {
      const data = await fetchWithAuth(`/admin/get-trades-by-id?ids=${newIds.join(",")}`, {
        credentials: "include"
      });

      // Track non-existent trades
      const nonExistentIds = newIds.filter(id => !data.trades.some(t => t.tradeId === Number(id)));
      if (nonExistentIds.length > 0) {
        setSnackbar(`Trade ID(s) not found: ${nonExistentIds.join(", ")}`);
      }

      // Add new trades to the existing list
      setTrades(prev => [...prev, ...data.trades]);
      setInput("");
    } catch (err) {
      setSnackbar(err.message);
      setInput("");
    }
  };

  const handleRemoveTrade = (tradeId) => {
    setTrades(trades.filter(t => t.tradeId !== tradeId));
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const idsToDelete = trades.map(t => t.tradeId);
      const result = await fetchWithAuth(`/admin/delete-trades`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: { ids: idsToDelete }
      });

      if (result.success) {
        setDeleteResult(`Successfully deleted ${idsToDelete.length} trade(s)`);
      } else {
        setDeleteResult(`Error: ${result.error}`);
      }
    } catch (err) {
      setDeleteResult(`Error deleting trades: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Typography paragraph>
        if u delete a trade, remember to post in the delete log in the server why u deleted it or i will hunt u down
      </Typography>
      {/* Input Row */}
      <Stack direction="row" spacing={1}>
        <TextField
          label="Trade IDs"
          value={input}
          onChange={handleInputChange}
          fullWidth
          onKeyDown={(e) => e.key === "Enter" && fetchTrades()}
          placeholder="e.g. 123, 456"
        />
        <Button variant="contained" onClick={fetchTrades}>Search</Button>
      </Stack>

      {/* Trades List */}
      <Box mt={2}>
        {trades.map((trade) => (
          <Paper key={trade.tradeId} sx={{ mb: 2 }}>
            <DeletableTradeCard
              trade={{
                ...trade,
                tradeDate: new Date(trade.tradeDate).toISOString().slice(0, 10) // format YYYY-MM-DD
              }}
              onRemove={() => handleRemoveTrade(trade.tradeId)}
            />
          </Paper>
        ))}
      </Box>

      {/* Delete Button */}
      {trades.length > 0 && (
        <Button
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={loading}
        >
          {loading ? "Deleting..." : "Delete"}
        </Button>
      )}

      {/* Confirm Modal */}
      <Dialog open={confirmOpen} onClose={() => !loading && setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteResult ? (
            <Typography>{deleteResult}</Typography>
          ) : (
            <>
              <Typography>
                Are you sure you want to delete? This cannot be undone! Please review carefully and confirm.
              </Typography>
              <Typography mt={1}>
                You are about to delete {trades.length} trade(s): {trades.map(t => t.tradeId).join(", ")}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!deleteResult && (
            <>
              <Button onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
              <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
                {loading ? "Deleting..." : "Yes, delete them"}
              </Button>
            </>
          )}
          {deleteResult && (
            <Button onClick={() => {
              setDeleteResult(null);
              setConfirmOpen(false);
              setTrades([]);
              setInput("");
            }}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </Box>
  );
};

export default AdminDeleteTrade;